import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Plus, Minus } from "lucide-react";

interface AccordionItem {
  title: string;
  image: string;
  problem: string;
  problemText: string;
  solution: string;
  solutionText: string;
  resultLine: string;
}

const items: AccordionItem[] = [
  {
    title: "Manualidade por Digitalização",
    image: "https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/oproblema/representation-user-experience-interface-design%20(2).jpg",
    problem: "Problema: Processos manuais que consomem seu tempo",
    problemText: "Propostas feitos manualmente, controles paralelos e retrabalho diário tornam a operação lenta, cansativa e pouco escalável. O tempo que deveria ser usado para vender e melhorar a entrega acaba sendo gasto com tarefas repetitivas.",
    solution: "Solução: Fluxo digital e automatizado",
    solutionText: "Substituímos processos manuais por um fluxo digital inteligente, automatizando propostas e etapas operacionais. Assim, você reduz erros, ganha velocidade e entrega eventos com mais eficiência e profissionalismo.",
    resultLine: "Resultado: mais tempo para vender, menos tempo perdido no operacional."
  },
  {
    title: "Estagnação por Crescimento Sustentável",
    image: "/images/business-partners-working-together.webp",
    problem: "Problema: A sensação de que o negócio não cresce",
    problemText: "Mesmo conquistando mais clientes ao longo dos anos, muitos donos de buffet sentem que o crescimento não é real. A dependência total do dono e processos manuais travam a evolução e limitam o potencial do negócio.",
    solution: "Solução: Estrutura para crescer com previsibilidade",
    solutionText: "Com processos organizados e digitalizados, o negócio deixa de depender exclusivamente do dono. Você cria tempo, estrutura e escala, alcança novos clientes e passa a crescer de forma sustentável, batendo metas com mais clareza.",
    resultLine: "Resultado: um negócio que cresce mesmo quando você não está presente."
  },
  {
    title: "Sobrevivência por Gestão Estratégica",
    image: "https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/oproblema/chefs-matching-pinstripe-chef-aprons-standing-side-by-side-modern-kitchen.jpg",
    problem: "Problema: Gerir um buffet com base no sentimento",
    problemText: "Sem dados, sem relatórios e sem visibilidade financeira, decisões são tomadas na intuição. Isso gera surpresas no caixa, dificuldade para precificar corretamente e impossibilidade de planejar o crescimento.",
    solution: "Solução: Gestão baseada em dados reais",
    solutionText: "O painel financeiro e os relatórios gerenciais trazem clareza sobre faturamento, eventos realizados, ticket médio e metas. Você para de adivinhar e começa a decidir com informação.",
    resultLine: "Resultado: visão baseada em dados, precificação correta e metas reais."
  }
];

const NubankVerticalAccordion = () => {
  // All open by default
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set([0, 1, 2]));

  const toggleIndex = (index: number) => {
    setOpenIndexes(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <section className="py-16 bg-muted">
      <div className="container-custom max-w-4xl mx-auto">
        {/* Título da seção */}
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2 text-center">
          Como nossa solução resolve cada um desses problemas
        </h2>
        <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
          Para cada dor que o dono de buffet sente no dia a dia, construímos uma
          solução direta — sem complexidade, sem necessidade de equipe técnica.
        </p>
        
        {/* Lista de itens */}
        <div>
          {items.map((item, index) => {
            const isOpen = openIndexes.has(index);
            return (
              <div key={index}>
                {index !== 0 && <div className="border-t border-border" />}
                
                {/* Título clicável */}
                <div 
                  onClick={() => toggleIndex(index)}
                  className="py-6 cursor-pointer group flex items-center gap-4"
                >
                  {isOpen ? (
                    <Minus className="w-6 h-6 text-primary flex-shrink-0" strokeWidth={2} />
                  ) : (
                    <Plus className="w-6 h-6 text-foreground group-hover:text-primary flex-shrink-0 transition-colors" strokeWidth={2} />
                  )}
                  <h3 className={`text-2xl md:text-3xl font-bold transition-colors ${
                    isOpen
                      ? 'text-primary'
                      : 'text-foreground group-hover:text-primary'
                  }`}>
                    {item.title}
                  </h3>
                </div>
                
                {/* Conteúdo expandido com animação */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
                        {/* Imagem à esquerda */}
                        <div className="rounded-[32px] overflow-hidden aspect-[4/3]">
                          <img 
                            src={item.image} 
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Texto à direita */}
                        <div className="flex flex-col justify-center space-y-4">
                          {/* Problema */}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <X className="w-4 h-4 text-destructive" strokeWidth={1.5} />
                              <span className="text-foreground font-medium">{item.problem}</span>
                            </div>
                            <p className="text-muted-foreground text-base leading-relaxed pl-6">
                              {item.problemText}
                            </p>
                          </div>
                          
                          {/* Solução */}
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Check className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                              <span className="text-foreground font-medium">{item.solution}</span>
                            </div>
                            <p className="text-muted-foreground text-base leading-relaxed pl-6">
                              {item.solutionText}
                            </p>
                            {/* Linha de resultado */}
                            <p className="pl-6 mt-2 font-bold text-primary">
                              {item.resultLine}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
          {/* Separador final */}
          <div className="border-t border-border" />
        </div>
      </div>
    </section>
  );
};

export default NubankVerticalAccordion;
